import { useEffect, useState } from 'react'

/**
 * Add-to-home-screen, where the browser offers it.
 *
 * B10's answer was: build the install prompt, plan for low take-up, keep email
 * primary for launch. This is that, and the "plan for low take-up" half is not
 * pessimism — it is the platform:
 *
 *   - Chrome and Edge on Android/desktop fire `beforeinstallprompt`, which is
 *     the only way to show an in-page install button. This hook catches it.
 *   - iOS Safari never fires it. There is no API. The user has to find Share →
 *     Add to Home Screen themselves, and on iOS that is not cosmetic: web push
 *     does not work at all until the site is on the home screen. So the people
 *     for whom installing matters most are exactly the ones this button cannot
 *     help.
 *
 * Which is why the button is absent rather than disabled when the event never
 * arrives. A permanently greyed-out "Install" on every iPhone in the company
 * teaches ~900 people that the app is broken. iOS guidance belongs in an
 * onboarding step with pictures, not in a topbar icon.
 */

/** Not in lib.dom yet — Chromium-only, and typed here rather than `any`. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      // Chromium shows its own mini-infobar unless this is prevented; we want
      // the prompt to happen on our button, in context, not on page load.
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    function onInstalled() {
      setDeferred(null)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  async function promptInstall() {
    if (!deferred) return
    await deferred.prompt()
    // The event is single-use. Whatever they chose, it cannot be shown again,
    // so drop it rather than leaving a button that silently does nothing.
    await deferred.userChoice
    setDeferred(null)
  }

  return { canInstall: deferred !== null, promptInstall }
}
